export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  CAD = 'CAD'
}

export enum BuyingPoint {
  PROGRAMMATIC = 'Programmatic',
  DIRECT = 'Direct',
  SOCIAL = 'Social'
}

export interface CalculatorSettings {
  totalBudget: number;
  sellCPM: number;
  displayCurrency: Currency; // Plan currency
  buyingPoint: BuyingPoint;
  /** Trading deal / buying point label persisted on the package (API `buyingPoint`). */
  tradingDealName: string;
  tradingDealId: string | null; // Selected trading deal ID
  tradingDealPercentage: number;
  hardCostsTotal: number;
}

export interface RateCard {
  id: string;
  country: string;
  channel: string;
  publisher: string;
  format: string;
  deliveryPercentage: number;
  rowCurrency: Currency; // Native currency for this row
  buyCPM: number;
  dspFeeId: string | null; // Selected DSP fee ID
  dspFeeName: string; // DSP name for display
  dspFeePercentage: number;

  // Calculated fields (in native currency)
  grossBudgetNative: number;
  netBudgetNative: number;
  impressions: number;
  publisherSpendNative: number;
  dspFeeValueNative: number;
  totalMediaSpendNative: number;
  allocatedHardCostNative: number;
  grossMarginNative: number;
  tradingDealAmountNative: number;
  grossProfitNative: number;
  /** Resolved rebate % for this row (channel override or deal default). */
  effectiveTradingDealPercentage: number;
  marginPercentage: number;
  profitPercentage: number;

  // Calculated fields (in plan/display currency)
  grossBudgetPlan: number;
  netBudgetPlan: number;
  publisherSpendPlan: number;
  dspFeeValuePlan: number;
  totalMediaSpendPlan: number;
  allocatedHardCostPlan: number;
  grossMarginPlan: number;
  tradingDealAmountPlan: number;
  grossProfitPlan: number;
}

export interface KPIMetrics {
  sellCPM: number;
  /** Sum of all rows' % delivery (Total Gross Budget = plan total × this ÷ 100). */
  totalDeliveryPercentage: number;
  totalGrossBudgetPlan: number;
  totalNetBudgetPlan: number;
  blendedMarginPercentage: number;
  blendedProfitPercentage: number;
  totalGrossMarginPlan: number;
  totalGrossProfitPlan: number;
}

// FX rates (base is GBP) — value = GBP per 1 unit of that currency
export interface FXRates {
  [Currency.USD]: number;
  [Currency.EUR]: number;
  [Currency.GBP]: number;
  [Currency.CAD]: number;
}

export type RateCardInput = Pick<
  RateCard,
  | 'country'
  | 'channel'
  | 'publisher'
  | 'format'
  | 'deliveryPercentage'
  | 'rowCurrency'
  | 'buyCPM'
  | 'dspFeeId'
  | 'dspFeeName'
  | 'dspFeePercentage'
>;

/** User-editable row fields only; calculated metrics are derived from settings + delivery %. */
export type StoredRateCard = RateCardInput & { id: string };

export interface UseEU6Calculator {
  settings: CalculatorSettings;
  rateCards: RateCard[];
  kpiMetrics: KPIMetrics;
  isModalOpen: boolean;
  fxRates: FXRates;
  updateSettings: (key: keyof CalculatorSettings, value: any) => void;
  updateFXRates: (rates: Partial<FXRates>) => void;
  addRateCard: (rateCard: RateCardInput) => void;
  duplicateRateCard: (rateCard: RateCard) => void;
  updateRateCard: (id: string, updates: Partial<RateCardInput>) => void;
  deleteRateCard: (id: string) => void;
  openModal: () => void;
  closeModal: () => void;
}
